import { z } from 'zod';
import { contactPhoneSchema, idSchema, moneySchema } from './common.schemas';
import { defineListQuery, inForceFilter, listFilter } from './list-query.schemas';
import { RATE_BASES } from './rate-card.schemas';

/** Job vocabulary: job types and their required documents (PRD → Build approach → Cargo; gitaumoses4/chukua#22). */

export const requiredDocumentTypeInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  name: z.string().trim().min(1).max(120),
});
export type RequiredDocumentTypeInput = z.infer<typeof requiredDocumentTypeInputSchema>;

export const JOB_TYPE_FAMILIES = ['container', 'loose'] as const;
export type JobTypeFamily = (typeof JOB_TYPE_FAMILIES)[number];

export const jobTypeInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  name: z.string().trim().min(1).max(120),
  family: z.enum(JOB_TYPE_FAMILIES),
  requiredDocumentTypeCodes: z.array(z.string().trim().min(1)).default([]),
  /** The driver needs any one of these licence classes (gitaumoses4/chukua#48); empty means no rule. */
  requiredLicenceClasses: z.array(z.string().trim().min(1).max(16)).default([]),
});
export type JobTypeInput = z.input<typeof jobTypeInputSchema>;

/**
 * A job's state machine (PRD → Job intake → States; gitaumoses4/chukua#29).
 * Planned/InProgress/Delivered are reached only through domain events from
 * dispatch/tracking (M3/M5) — no request shape here ever sets them directly.
 */
export const JOB_STATUSES = ['enquiry', 'quoted', 'confirmed', 'planned', 'in_progress', 'delivered', 'closed', 'on_hold', 'cancelled'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

/** How an enquiry reached the office (PRD → Job intake; gitaumoses4/chukua#30). */
export const JOB_SOURCES = ['phone', 'whatsapp', 'email', 'release_order', 'walk_in'] as const;
export type JobSource = (typeof JOB_SOURCES)[number];

/** Where a job's agreed price came from (PRD → Job intake → Commercial terms; gitaumoses4/chukua#33): looked up on a rate card, or typed in off-card. */
export const JOB_PRICE_SOURCES = ['rate_card', 'manual'] as const;
export type JobPriceSource = (typeof JOB_PRICE_SOURCES)[number];

/** Who bears a cost — tolls, weighbridge — on a job (gitaumoses4/chukua#33). Applies to container and loose jobs alike, so it is not `LOOSE_CARGO_RESPONSIBILITIES`. */
export const JOB_COST_RESPONSIBILITIES = ['customer', 'us', 'third_party'] as const;
export type JobCostResponsibility = (typeof JOB_COST_RESPONSIBILITIES)[number];

/** Price, basis and source describe one quote, so they come together or not at all. */
const hasWholePrice = (job: { agreedPrice?: unknown; rateBasis?: unknown; priceSource?: unknown }) =>
  [job.agreedPrice, job.rateBasis, job.priceSource].every((field) => field === undefined) ||
  [job.agreedPrice, job.rateBasis, job.priceSource].every((field) => field !== undefined);

const wholePriceIssue = { message: 'Agreed price, rate basis and price source go together', path: ['agreedPrice'] };

/**
 * `POST /jobs` — quick-create (gitaumoses4/chukua#30): only `customerId`/`jobTypeCode`
 * are required, a job is created in Enquiry, and status is never a field here. Everything
 * else is fillable now or later through `PATCH /jobs/:id` (`updateJobSchema`).
 */
const createJobFields = z.object({
  customerId: z.string().uuid(),
  jobTypeCode: z.string().trim().min(1).max(32),
  originZoneCode: z.string().trim().min(1).max(32).optional(),
  destinationZoneCode: z.string().trim().min(1).max(32).optional(),
  requestedDate: z.iso.date().optional(),
  customerDeliveryDeadline: z.iso.date().optional(),
  source: z.enum(JOB_SOURCES).optional(),
  contactName: z.string().trim().min(1).max(120).optional(),
  contactPhone: contactPhoneSchema.optional(),
  requestedQuantity: z.number().int().positive().max(999).optional(),
  /** Commercial terms (gitaumoses4/chukua#33) — copied onto the job, never looked up live later. */
  agreedPrice: moneySchema.refine((money) => money.amountMinor >= 0, { message: 'Amount cannot be negative', path: ['amountMinor'] }).optional(),
  rateBasis: z.enum(RATE_BASES).optional(),
  priceSource: z.enum(JOB_PRICE_SOURCES).optional(),
  tollsPayer: z.enum(JOB_COST_RESPONSIBILITIES).optional(),
  weighbridgePayer: z.enum(JOB_COST_RESPONSIBILITIES).optional(),
  cancellationTerms: z.string().trim().min(1).max(1000).optional(),
  /** Overrides the customer's own payment terms for this job only. */
  paymentTermsDaysOverride: z.number().int().positive().max(365).optional(),
  poNumber: z.string().trim().min(1).max(64).optional(),
});

export const createJobSchema = createJobFields.refine(hasWholePrice, wholePriceIssue);
export type CreateJobInput = z.infer<typeof createJobSchema>;

/** `PATCH /jobs/:id` — fills in whatever quick-create left blank. `customerId`/`jobTypeCode` are immutable post-create. */
export const updateJobSchema = createJobFields.omit({ customerId: true, jobTypeCode: true }).refine(hasWholePrice, wholePriceIssue);
export type UpdateJobInput = z.infer<typeof updateJobSchema>;

/** `POST /jobs/:id/price` — prices the job from the rate card (gitaumoses4/chukua#25). `basis` settles an ambiguous answer; `reprice` replaces a price the job already has. */
export const priceJobInputSchema = z.object({
  basis: z.enum(RATE_BASES).optional(),
  reprice: z.boolean().default(false),
});
export type PriceJobInput = z.infer<typeof priceJobInputSchema>;

/**
 * Why a job is held or cancelled (gitaumoses4/chukua#40) — an admin-managed code, tagged for
 * `hold`, `cancel`, or both. Alongside, not instead of, the free-text reason below.
 */
const jobReasonCodeInputFields = z.object({
  code: z.string().trim().min(1).max(32),
  name: z.string().trim().min(1).max(120),
  appliesToHold: z.boolean(),
  appliesToCancel: z.boolean(),
});

export const jobReasonCodeInputSchema = jobReasonCodeInputFields.refine((input) => input.appliesToHold || input.appliesToCancel, {
  message: 'A reason code must apply to hold, cancel, or both',
  path: ['appliesToHold'],
});
export type JobReasonCodeInput = z.infer<typeof jobReasonCodeInputSchema>;

export interface JobReasonCodeView {
  id: string;
  code: string;
  name: string;
  appliesToHold: boolean;
  appliesToCancel: boolean;
  isActive: boolean;
}

/** `POST /jobs/:id/{cancel,hold}` — `reasonCode` is matched against `jobReasonCodeInputSchema`'s catalogue, tagged for the action; `reason` stays free text, same shape as `recordOverrideSchema`'s reason field. */
export const jobReasonSchema = z.object({
  reasonCode: z.string().trim().min(1).max(32),
  reason: z.string().trim().min(1).max(500),
});
export type JobReasonInput = z.infer<typeof jobReasonSchema>;

/** `POST /jobs/:id/reject-quote` — the customer's reason, kept on the quote. No reason code: rejecting a quote is a decision on the quote, not the office cancelling a job (gitaumoses4/chukua#34). */
export const rejectQuoteInputSchema = z.object({ reason: z.string().trim().min(1).max(500) });
export type RejectQuoteInput = z.infer<typeof rejectQuoteInputSchema>;

/** Compared with today's UTC date, same idiom as `empty-return.schemas.ts`'s `isNotFuture` — a quote valid "until today" is still accepted. */
function isNotPast(isoDate: string): boolean {
  return isoDate >= new Date().toISOString().slice(0, 10);
}

/**
 * `POST /jobs/:id/quote` — quotes a job already priced (`agreedPrice` set) with a
 * validity date (PRD → Job intake → States; gitaumoses4/chukua#34). Replaces the
 * previous bare, no-body POST.
 */
export const quoteJobInputSchema = z.object({
  validUntil: z.iso.date().refine(isNotPast, 'Quote validity cannot be in the past'),
});
export type QuoteJobInput = z.infer<typeof quoteJobInputSchema>;

/** How a customer's acceptance reached the office — same idiom as JOB_SOURCES. */
export const QUOTE_ACCEPTANCE_CHANNELS = ['phone', 'whatsapp', 'email', 'written', 'in_person'] as const;
export type QuoteAcceptanceChannel = (typeof QUOTE_ACCEPTANCE_CHANNELS)[number];

/** `POST /jobs/:id/accept-quote` — records who/when/channel; `rejectQuoteInputSchema` takes the rejection reason. `acceptedByName` is the customer contact who accepted, not the office user recording it (`decidedBy`, read off the session). */
export const acceptQuoteInputSchema = z.object({
  acceptedByName: z.string().trim().min(1).max(120),
  acceptanceChannel: z.enum(QUOTE_ACCEPTANCE_CHANNELS),
});
export type AcceptQuoteInput = z.infer<typeof acceptQuoteInputSchema>;

/** A quote's decision (gitaumoses4/chukua#34) — `pending` until accepted or rejected. */
export const QUOTE_DECISIONS = ['pending', 'accepted', 'rejected'] as const;
export type QuoteDecision = (typeof QUOTE_DECISIONS)[number];

/** Every manual action a job's state machine (`job-state.policy.ts`) exposes — the source of `JobAction`, not `job-state.policy.ts` itself. */
export const JOB_ACTIONS = ['quote', 'confirm', 'cancel', 'hold', 'resume', 'close'] as const;
export type JobAction = (typeof JOB_ACTIONS)[number];

/** Why a job is `on_hold` (gitaumoses4/chukua#37) — `manual` is the `hold` action, `credit` is the confirm-time credit check. Null off `on_hold`. */
export const HOLD_REASONS = ['manual', 'credit'] as const;
export type JobHoldReason = (typeof HOLD_REASONS)[number];

/**
 * `POST /jobs/:id/credit-hold-decision` — a manager's decision on a credit hold (gitaumoses4/chukua#37):
 * approve it back onto the job's confirmed path (reason required, since it overrides a flag), or keep
 * holding (reason optional — often just "still investigating").
 */
export const creditHoldDecisionSchema = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('approved'), reason: z.string().trim().min(1).max(500) }),
  z.object({ decision: z.literal('held'), reason: z.string().trim().max(500).optional() }),
]);
export type CreditHoldDecisionInput = z.infer<typeof creditHoldDecisionSchema>;

/** How close a job's `customerDeliveryDeadline` is, against the configurable threshold (`OrganizationSettings.jobDueSoonThresholdDays`). Absent when the job has no deadline, is closed/cancelled, or is comfortably on time. */
export type JobDeadlinePressure = 'overdue' | 'due_soon';

/** `GET /jobs` — search matches job number and customer name (gitaumoses4/chukua#39). Container number and BL land with the cargo data model (a follow-up). */
export const jobListQuerySchema = defineListQuery({
  sortFields: ['number', 'status', 'deadline', 'requestedDate', 'quoteValidUntil', 'heldAt'],
  defaultSort: { sort: 'number', order: 'desc' },
  filters: {
    status: listFilter(z.enum(JOB_STATUSES)),
    customerId: listFilter(idSchema),
    jobTypeCode: listFilter(z.string().trim().min(1).max(32)),
    /** Jobs left out of the page and the total — the dispatch board's per-session "Skip" (#55) keeps paging right this way. */
    excludeId: listFilter(idSchema),
    requestedDateFrom: z.iso.date().optional(),
    requestedDateTo: z.iso.date().optional(),
    deadlineFrom: z.iso.date().optional(),
    deadlineTo: z.iso.date().optional(),
    /** A job with a pending quote whose `validUntil` is past (`true`) or not (`false`) — a job with no quote matches neither. */
    quoteExpired: inForceFilter,
  },
});
export type JobListQuery = z.infer<typeof jobListQuerySchema>;
